import { useState, useEffect, useCallback } from 'react';
import { casesData } from '@/mocks/cases';

const testimonials = casesData.map((c) => ({
  name: c.testimonialAuthor,
  role: c.testimonialRole,
  text: c.testimonial,
  category: c.category,
  project: c.title,
  location: c.location,
  year: c.year,
  img: c.coverImg,
}));

export default function TestimonialsSection() {
  const [active, setActive] = useState(0);
  const [animating, setAnimating] = useState(false);
  const total = testimonials.length;

  const goTo = useCallback(
    (index: number) => {
      if (animating) return;
      setAnimating(true);
      setTimeout(() => {
        setActive((index + total) % total);
        setAnimating(false);
      }, 250);
    },
    [animating, total],
  );

  const prev = () => goTo(active - 1);
  const next = useCallback(() => goTo(active + 1), [active, goTo]);

  // Auto-play
  useEffect(() => {
    const timer = setInterval(() => {
      next();
    }, 5500);
    return () => clearInterval(timer);
  }, [next]);

  const current = testimonials[active];

  return (
    <section className="py-24 md:py-32 bg-[#1a1a1a] overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <p className="text-[#b8956a] text-sm font-medium tracking-[0.2em] uppercase mb-4">
              Client Testimonials
            </p>
            <h2
              className="font-bold text-white leading-[1.35]"
              style={{ fontSize: 'clamp(1.5rem, 3vw, 2.125rem)' }}
            >
              業主真實回饋
            </h2>
            <p className="text-white/45 mt-3" style={{ fontSize: '1rem' }}>
              {total} 位業主・真實見證空間帶來的改變
            </p>
          </div>

          {/* Navigation arrows */}
          <div className="flex items-center gap-3">
            <button
              onClick={prev}
              className="w-11 h-11 flex items-center justify-center border border-white/20 rounded-full text-white/60 hover:border-[#b8956a] hover:text-[#b8956a] transition-colors cursor-pointer"
              aria-label="上一則"
            >
              <i className="ri-arrow-left-line text-base"></i>
            </button>
            <button
              onClick={next}
              className="w-11 h-11 flex items-center justify-center border border-white/20 rounded-full text-white/60 hover:border-[#b8956a] hover:text-[#b8956a] transition-colors cursor-pointer"
              aria-label="下一則"
            >
              <i className="ri-arrow-right-line text-base"></i>
            </button>
          </div>
        </div>

        {/* Main layout */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-10">

          {/* Featured testimonial */}
          <div className="lg:col-span-3">
            <div
              className="bg-white/5 border border-white/10 rounded-2xl p-8 md:p-10 h-full flex flex-col justify-between transition-opacity duration-250"
              style={{ opacity: animating ? 0 : 1 }}
            >
              {/* Quote + text */}
              <div>
                <i className="ri-double-quotes-l text-[#b8956a] text-4xl block mb-6"></i>
                <p
                  className="text-white/85 leading-[1.85]"
                  style={{ fontSize: 'clamp(1rem, 1.5vw, 1.1875rem)' }}
                >
                  {current.text}
                </p>
              </div>

              {/* Author */}
              <div className="mt-8 pt-7 border-t border-white/10 flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-px bg-[#b8956a]"></div>
                  <div>
                    <p className="text-white font-semibold">{current.name}</p>
                    <p className="text-[#b8956a] text-sm mt-0.5">{current.role}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-white/35 text-xs">
                  <span className="px-2.5 py-1 bg-white/8 rounded-full border border-white/10">
                    {current.category}
                  </span>
                  <span>{current.location}</span>
                  <span>·</span>
                  <span>{current.year}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Side: project image + other quotes */}
          <div className="lg:col-span-2 flex flex-col gap-4">
            {/* Project cover image */}
            <div
              className="relative w-full h-[200px] lg:h-[220px] rounded-2xl overflow-hidden transition-opacity duration-250"
              style={{ opacity: animating ? 0 : 1 }}
            >
              <img
                src={current.img}
                alt={current.project}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent"></div>
              <div className="absolute bottom-4 left-4">
                <p className="text-white font-semibold text-sm">{current.project}</p>
                <p className="text-white/55 text-xs mt-0.5 flex items-center gap-1">
                  <i className="ri-map-pin-line"></i>
                  {current.location}
                </p>
              </div>
            </div>

            {/* Dot indicators */}
            <div className="flex flex-wrap gap-2 px-1">
              {testimonials.map((t, i) => (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  title={t.name}
                  className={`transition-all duration-300 rounded-full cursor-pointer ${
                    i === active
                      ? 'w-7 h-2 bg-[#b8956a]'
                      : 'w-2 h-2 bg-white/25 hover:bg-white/50'
                  }`}
                  aria-label={`切換至 ${t.name}`}
                />
              ))}
            </div>

            {/* Mini cards for adjacent testimonials */}
            <div className="flex flex-col gap-3 flex-1">
              {[1, 2].map((offset) => {
                const idx = (active + offset) % total;
                const t = testimonials[idx];
                return (
                  <button
                    key={idx}
                    onClick={() => goTo(idx)}
                    className="text-left bg-white/4 border border-white/8 hover:border-white/20 rounded-xl p-4 transition-colors cursor-pointer"
                  >
                    <p className="text-white/55 text-xs leading-relaxed line-clamp-2 mb-2.5">
                      {t.text}
                    </p>
                    <div className="flex items-center gap-2">
                      <span className="w-4 h-px bg-[#b8956a] block flex-shrink-0"></span>
                      <p className="text-white/70 text-xs font-medium">{t.name}</p>
                      <span className="text-white/25 text-xs">·</span>
                      <p className="text-[#b8956a] text-xs">{t.role.split(' ')[0]}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Bottom stats */}
        <div className="mt-14 pt-10 border-t border-white/10 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { value: '8+', label: '完成案例', icon: 'ri-building-line' },
            { value: '4.9', label: '平均客戶評分', icon: 'ri-star-line' },
            { value: '100%', label: '準時完工率', icon: 'ri-time-line' },
            { value: '38%', label: '業績平均提升', icon: 'ri-line-chart-line' },
          ].map((s) => (
            <div key={s.label} className="flex items-center gap-4">
              <div className="w-10 h-10 flex items-center justify-center bg-[#b8956a]/15 rounded-xl flex-shrink-0">
                <i className={`${s.icon} text-[#b8956a] text-base`}></i>
              </div>
              <div>
                <p className="text-white font-bold" style={{ fontSize: '1.5rem' }}>{s.value}</p>
                <p className="text-white/45 text-xs mt-0.5">{s.label}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
